import * as THREE from "three";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";
import { USDZExporter } from "three/examples/jsm/exporters/USDZExporter.js";

import {
    describeAndroidHostedModelSource,
    describeIosHostedModelSource,
    resolveAndroidHostedModelUrl,
    resolveIosHostedModelUrl,
} from "./arHosting.service";

const getUserAgent = () => {
    if (typeof window === "undefined") return "";
    return window.navigator.userAgent || "";
};

export const getARPlatform = () => {
    const userAgent = getUserAgent();

    if (/android/i.test(userAgent)) {
        return "android";
    }

    if (/iPad|iPhone|iPod/i.test(userAgent)) {
        return "ios";
    }

    return "desktop";
};

export const getHostedARModelUrls = (options = {}) => ({
    androidModelUrl:
        resolveAndroidHostedModelUrl(options) ||
        process.env.REACT_APP_AR_MODEL_URL ||
        "",
    androidModelSource: describeAndroidHostedModelSource(options),
    iosModelUrl: resolveIosHostedModelUrl(options),
    iosModelSource: describeIosHostedModelSource(options),
});

const cloneMaterial = (material) => {
    if (!material) return material;
    if (Array.isArray(material)) {
        return material.map((item) => (item?.clone ? item.clone() : item));
    }

    return material.clone ? material.clone() : material;
};

// Names to exclude from ALL AR exports (GLB + USDZ)
const AR_EXCLUDE_NAMES_GLOBAL = /leg|rail|base-rail|wallname|label|manufacturerLogo/i;

const isExcludedGlobal = (obj) => {
    let cur = obj;
    while (cur) {
        if (cur.userData?.excludeFromAR) return true;
        if (cur.name && AR_EXCLUDE_NAMES_GLOBAL.test(cur.name)) return true;
        cur = cur.parent;
    }
    return false;
};

const buildExportScene = (scene, cameraPosition) => {
    const sourceGroup = scene?.getObjectByName("fullBuilding");

    if (!sourceGroup) {
        throw new Error("The building scene is not ready yet.");
    }

    // Force-update world matrices to get correct positions including Bounds transform
    scene.updateMatrixWorld(true);

    // Collect world-space meshes — same approach as USDZ export so GLB and
    // USDZ are identical in scale/position. model-viewer treats 1 GLB unit = 1 metre,
    // so we must apply FEET_TO_METERS here too (raw feet = 20+ metre building in AR).
    const worldMeshes = [];
    sourceGroup.traverse((child) => {
        if (!child.isMesh || !child.geometry) return;
        if (isExcludedGlobal(child)) return;
        const geo = child.geometry.clone();
        geo.applyMatrix4(child.matrixWorld);
        worldMeshes.push({ geo, material: cloneMaterial(child.material) });
    });

    if (!worldMeshes.length) {
        throw new Error("No mesh geometry found in the building scene.");
    }

    // Compute bounding box from actual vertex positions
    const box = new THREE.Box3();
    const _v = new THREE.Vector3();
    worldMeshes.forEach(({ geo }) => {
        const pos = geo.attributes.position;
        if (!pos) return;
        for (let i = 0; i < pos.count; i++) {
            _v.fromBufferAttribute(pos, i);
            box.expandByPoint(_v);
        }
    });

    const centre = new THREE.Vector3();
    box.getCenter(centre);

    // Rotate the model so the face closest to the user's current camera view
    // becomes the "front" in AR — prevents the model always appearing from one fixed angle.
    let cameraYaw = 0;
    if (cameraPosition) {
        cameraYaw = Math.atan2(
            cameraPosition.x - centre.x,
            cameraPosition.z - centre.z
        );
    }

    // Apply centering + camera-yaw rotation + feet→metres scale (same as USDZ)
    const translateMat = new THREE.Matrix4().makeTranslation(-centre.x, -box.min.y, -centre.z);
    const rotateMat = new THREE.Matrix4().makeRotationY(cameraYaw);
    const scaleMat = new THREE.Matrix4().makeScale(FEET_TO_METERS, FEET_TO_METERS, FEET_TO_METERS);
    // Order: translate to centre, rotate to match camera view, then scale
    const finalMat = new THREE.Matrix4()
        .multiplyMatrices(scaleMat, new THREE.Matrix4().multiplyMatrices(rotateMat, translateMat));

    const exportScene = new THREE.Scene();
    worldMeshes.forEach(({ geo, material }, i) => {
        geo.applyMatrix4(finalMat);
        const mesh = new THREE.Mesh(geo, material);
        mesh.name = `building_mesh_${i}`;
        mesh.frustumCulled = false;
        exportScene.add(mesh);
    });

    exportScene.updateMatrixWorld(true);
    return exportScene;
};

/**
 * Convert any material to MeshStandardMaterial for USDZExporter.
 *
 * Key rules for reliable USDZ output:
 * - USDZExporter ONLY processes MeshStandardMaterial; all others are skipped.
 * - Textures are included IF the image is already loaded into GPU memory
 *   (texture.image exists as HTMLImageElement/Canvas/ImageBitmap).
 *   External URLs that are not yet loaded are skipped to avoid transparency.
 * - Force fully opaque so ARKit has solid geometry to anchor to a surface.
 */
const isTextureReady = (texture) => {
    if (!texture || !texture.image) return false;
    const img = texture.image;
    // HTMLImageElement — check it finished loading
    if (img.complete !== undefined) return img.complete && img.naturalWidth > 0;
    // Canvas, ImageBitmap, VideoFrame — always ready
    return true;
};

const toStandardMaterial = (mat) => {
    if (!mat) return new THREE.MeshStandardMaterial({ color: 0xaaaaaa, roughness: 0.7, metalness: 0.1 });

    // Determine base color — works for Phong, Lambert, Basic, Standard, Toon
    const color = mat.color ? mat.color.clone() : new THREE.Color(0xaaaaaa);

    // Include the color/diffuse map if it is fully loaded in memory.
    // USDZExporter can embed loaded image data directly into the zip.
    // If the texture is NOT loaded yet, fall back to the solid color.
    const map = isTextureReady(mat.map) ? mat.map : null;

    // If color is pure white AND a texture provides the actual appearance,
    // keep white so the texture renders correctly (white * texture = texture).
    // Only fall back to grey if there is no usable texture.
    const isWhite = color.r > 0.98 && color.g > 0.98 && color.b > 0.98;
    if (isWhite && !map) {
        color.setHex(0xcccccc); // neutral grey so mesh isn't invisible
    }

    return new THREE.MeshStandardMaterial({
        color,
        map,                    // null if texture not loaded → solid color
        roughness: 0.6,
        metalness: 0.2,
        // Force fully opaque — transparent geometry prevents ARKit surface anchoring
        transparent: false,
        opacity: 1,
        side: mat.side ?? THREE.FrontSide,
    });
};

const convertMaterialsForUsdz = (material) => {
    if (!material) return new THREE.MeshStandardMaterial({ color: 0xcccccc });
    if (Array.isArray(material)) return material.map(toStandardMaterial);
    return toStandardMaterial(material);
};

// Three.js scene uses feet as its unit (1 unit = 1 foot).
// USDZ / ARKit expects meters (1 unit = 1 metre).
// 1:30 scale: a 30ft building → ~30cm model. Visible on the floor without
// filling the screen. Adjust this constant to change the model size.
const FEET_TO_METERS = 0.3048 / 10; // 1:10 model scale (~3cm per foot, more visible in AR)

/**
 * Build an export scene where EVERY material is MeshStandardMaterial,
 * geometry is correctly scaled to real-world metres, and the building
 * is centred on the AR floor plane so it stands upright facing the user.
 *
 * IMPORTANT: We bake the scale and centering transform DIRECTLY into each
 * mesh's geometry (applyMatrix4 on BufferGeometry). This is the only
 * reliable approach — relying on Group.scale + updateMatrixWorld() is
 * fragile because USDZExporter reads matrixWorld per-mesh using the mesh's
 * own accumulated world matrix, and group-level scale can be silently
 * ignored or misread depending on the Three.js / exporter version.
 */
const buildUsdzExportScene = (scene, cameraPosition) => {
    const sourceGroup = scene?.getObjectByName("fullBuilding");

    if (!sourceGroup) {
        throw new Error("The building scene is not ready yet.");
    }

    // ── Step 1: force-update world matrices of the LIVE scene ────────────
    // The fullBuilding group may be inside a <Bounds> wrapper (react-three/drei)
    // which applies a camera-fitting transform. We must read the LIVE world
    // matrices (not local matrices) so we capture the real vertex positions.
    scene.updateMatrixWorld(true);

    // ── Step 2: collect all meshes with their world-space vertex data ─────
    const worldMeshes = [];
    sourceGroup.traverse((child) => {
        if (!child.isMesh || !child.geometry) return;
        if (isExcludedGlobal(child)) return; // skip legs, labels, watermarks
        const geo = child.geometry.clone();
        // applyMatrix4 with the mesh's LIVE world matrix converts all vertex
        // positions from local mesh space → world space in one step.
        geo.applyMatrix4(child.matrixWorld);
        worldMeshes.push({ geo, material: convertMaterialsForUsdz(child.material) });
    });

    if (!worldMeshes.length) {
        throw new Error(`No mesh geometry found in the building scene. Make sure the 3D building is fully loaded before exporting.`);
    }
    console.log(`[AR Export] Found ${worldMeshes.length} meshes in fullBuilding group`);

    // ── Step 3: compute bounding box over ALL world-space geometry ────────
    const box = new THREE.Box3();
    worldMeshes.forEach(({ geo }) => box.expandByObject(
        Object.assign(new THREE.Mesh(geo), { matrixWorld: new THREE.Matrix4() })
    ));
    // Simpler: use position attribute directly
    box.makeEmpty();
    const _v = new THREE.Vector3();
    worldMeshes.forEach(({ geo }) => {
        const pos = geo.attributes.position;
        if (!pos) return;
        for (let i = 0; i < pos.count; i++) {
            _v.fromBufferAttribute(pos, i);
            box.expandByPoint(_v);
        }
    });

    const centre = new THREE.Vector3();
    box.getCenter(centre);

    // ── Step 4: build centering + camera-yaw rotation + scale matrix ──────
    // Rotate the model so the face closest to the user's current camera view
    // becomes the "front" in AR — prevents the model always appearing from one fixed angle.
    let cameraYaw = 0;
    if (cameraPosition) {
        cameraYaw = Math.atan2(
            cameraPosition.x - centre.x,
            cameraPosition.z - centre.z
        );
    }

    // Translate: geometric centre → (0, 0, 0), base → Y = 0
    // Rotate: align front face to match mobile camera angle
    // Scale:  feet → metres at 1:30 model scale
    const translateMat = new THREE.Matrix4().makeTranslation(
        -centre.x,
        -box.min.y,
        -centre.z
    );
    const rotateMat = new THREE.Matrix4().makeRotationY(cameraYaw);
    const scaleMat = new THREE.Matrix4().makeScale(
        FEET_TO_METERS, FEET_TO_METERS, FEET_TO_METERS
    );
    // Order: translate to centre, rotate to match camera view, then scale
    const finalMat = new THREE.Matrix4()
        .multiplyMatrices(scaleMat, new THREE.Matrix4().multiplyMatrices(rotateMat, translateMat));

    // ── Step 5: apply final matrix and build flat export scene ────────────
    // Each mesh is now a standalone Mesh at identity transform.
    // Vertex positions ARE the final export positions — nothing left for the
    // exporter to misread from object transforms.
    const exportScene = new THREE.Scene();
    worldMeshes.forEach(({ geo, material }, i) => {
        geo.applyMatrix4(finalMat);
        const mesh = new THREE.Mesh(geo, material);
        mesh.name = `building_mesh_${i}`;
        mesh.frustumCulled = false;
        exportScene.add(mesh);
    });

    exportScene.updateMatrixWorld(true);
    return exportScene;
};

export const exportBuildingSceneToGlb = (scene, fileName = "sensei-building.glb", cameraPosition) =>
    new Promise((resolve, reject) => {
        try {
            const exportScene = buildExportScene(scene, cameraPosition);
            const exporter = new GLTFExporter();

            exporter.parse(
                exportScene,
                (result) => {
                    if (!(result instanceof ArrayBuffer)) {
                        reject(new Error("The AR export did not return a GLB file."));
                        return;
                    }

                    const file = new File([result], fileName, {
                        type: "model/gltf-binary",
                    });

                    resolve(file);
                },
                (error) => reject(error),
                {
                    binary: true,
                    onlyVisible: true,
                    trs: false,
                }
            );
        } catch (error) {
            reject(error);
        }
    });

/**
 * Export the current Three.js building scene as a USDZ file.
 * USDZ is the format required by iOS Quick Look for AR placement.
 * Returns a { file, blobUrl } object — blobUrl can be set directly as
 * the href of an <a rel="ar"> element so the user can tap it natively.
 */
export const exportBuildingSceneToUsdz = async (scene, fileName = "sensei-building.usdz", cameraPosition) => {
    // Use the USDZ-specific scene builder that converts ALL materials to
    // MeshStandardMaterial. Other material types are silently skipped by
    // USDZExporter, producing a transparent/invisible model in Quick Look.
    const exportScene = buildUsdzExportScene(scene, cameraPosition);
    const exporter = new USDZExporter();

    // USDZExporter.parse() is async and returns an ArrayBuffer (Uint8Array)
    const arraybuffer = await exporter.parse(exportScene, {
        quickLookCompatible: true,
        ar: {
            anchoring: { type: "plane" },
            planeAnchoring: { alignment: "horizontal" },
        },
    });

    const file = new File([arraybuffer], fileName, { type: "model/vnd.usdz+zip" });
    const blobUrl = URL.createObjectURL(file);

    return { file, blobUrl };
};

export const downloadArModel = (file) => {
    const objectUrl = URL.createObjectURL(file);
    const link = document.createElement("a");

    link.href = objectUrl;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();

    setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
};

export const canShareArModelFile = (file) => {
    if (!file || !navigator?.canShare) return false;

    try {
        return navigator.canShare({ files: [file] });
    } catch (error) {
        return false;
    }
};

export const shareArModelFile = async (file) => {
    if (!canShareArModelFile(file)) {
        return false;
    }

    await navigator.share({
        title: file.name,
        text: "Open this building model in a compatible AR viewer.",
        files: [file],
    });

    return true;
};

const buildSceneViewerIntentUrl = (modelUrl, title) => {
    const encodedModelUrl = encodeURIComponent(modelUrl);
    const encodedTitle = encodeURIComponent(title);
    const fallbackUrl = encodeURIComponent(modelUrl);

    return `intent://arvr.google.com/scene-viewer/1.0?file=${encodedModelUrl}&mode=ar_preferred&title=${encodedTitle}#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=${fallbackUrl};end;`;
};

export const looksLikeUsdzUrl = (modelUrl = "") =>
    /\.usdz(\?|#|$)/i.test(String(modelUrl || "").trim());

/**
 * Android only — opens Google Scene Viewer via intent URL.
 * iOS Quick Look must NOT be launched via JS. Instead, render an
 * <a rel="ar"><img></a> element directly in JSX and let the user tap it.
 */
export const openAndroidSceneViewer = (androidModelUrl, title = "Sensei Building") => {
    if (!androidModelUrl) return { ok: false, reason: "No Android GLB URL configured." };
    window.location.href = buildSceneViewerIntentUrl(androidModelUrl, title);
    return { ok: true, reason: "" };
};
